// Helper functions for room operations

import { roomsAPI } from '../services/api';
import axios from 'axios';

/**
 * Handle edit room
 * @param {Object} room - The room object to edit
 * @param {Array} hotels - Array of hotel objects
 * @param {Function} setEditingRoom - State setter for editing room
 * @param {Function} setSelectedHotelForRoomSearch - State setter for selected hotel for room search
 * @param {Function} setTravelomatixRoomData - State setter for Travelomatix room data
 * @param {Function} setTravelomatixRoomFormData - State setter for Travelomatix room form data
 * @param {Function} setShowTravelomatixRoomForm - State setter for showing Travelomatix room form
 * @param {Function} initializeRoomFormDataForEdit - Function to initialize room form data for edit
 */
export const handleEditRoom = (room, hotels, setEditingRoom, setSelectedHotelForRoomSearch, setTravelomatixRoomData, setTravelomatixRoomFormData, setShowTravelomatixRoomForm, initializeRoomFormDataForEdit) => {
  setEditingRoom(room);
  
  // Set the selected hotel for room search (needed for the form)
  const hotel = hotels.find(h => h.id === room.hotel_id);
  if (hotel) {
    setSelectedHotelForRoomSearch(hotel);
  }
  
  // Set the Travelomatix room data to the existing room data
  setTravelomatixRoomData(room);
  
  // Initialize form data with existing room data
  setTravelomatixRoomFormData(initializeRoomFormDataForEdit(room));
  
  setShowTravelomatixRoomForm(true);
};

/**
 * Handle delete room
 * @param {string} id - The room ID to delete
 * @param {string} hotelId - The hotel ID
 * @param {Function} setErrors - State setter for errors
 * @param {Function} fetchRoomsForHotel - Function to fetch rooms for a hotel
 */
export const handleDeleteRoom = async (id, hotelId, setErrors, fetchRoomsForHotel) => {
  try {
    await roomsAPI.delete(id);
    // Fetch updated rooms for this hotel
    await fetchRoomsForHotel(hotelId);
  } catch (error) {
    console.error('Error deleting room:', error);
    setErrors(prev => ({ ...prev, deleteRoom: error.message || 'Failed to delete room' }));
  }
};

/**
 * Add room from Travelomatix to local database
 * @param {Object} roomData - The room data from Travelomatix
 * @param {Object} selectedHotelForRoomSearch - The selected hotel for room search
 * @param {Function} setTravelomatixRoomData - State setter for Travelomatix room data
 * @param {Function} setTravelomatixRoomFormData - State setter for Travelomatix room form data
 * @param {Function} setShowTravelomatixRoomForm - State setter for showing Travelomatix room form
 * @param {Function} setShowRoomSearchResults - State setter for showing room search results
 * @param {Function} initializeRoomFormDataForAdd - Function to initialize room form data for add
 */
export const addRoomFromTravelomatix = (roomData, selectedHotelForRoomSearch, setTravelomatixRoomData, setTravelomatixRoomFormData, setShowTravelomatixRoomForm, setShowRoomSearchResults, initializeRoomFormDataForAdd) => {
  // Instead of automatically adding the room, show a form in a popup
  setTravelomatixRoomData(roomData);
  
  // Initialize form data with Travelomatix data and hotel info
  setTravelomatixRoomFormData(initializeRoomFormDataForAdd(selectedHotelForRoomSearch, roomData));
  
  setShowTravelomatixRoomForm(true);
  // Close the room search results
  setShowRoomSearchResults(false);
};

/**
 * Handle room form submission with file upload
 * @param {Object} e - The event object
 * @param {Function} validateRoomForm - Function to validate room form
 * @param {Object} roomFormData - Current room form data
 * @param {Function} setFormErrors - State setter for form errors
 * @param {Function} setErrors - State setter for errors
 * @param {Function} fetchRoomsForHotel - Function to fetch rooms for a hotel
 * @param {Object} editingRoom - Current editing room
 * @param {Function} resetRoomForm - Function to reset room form
 */
export const handleRoomSubmit = async (e, validateRoomForm, roomFormData, setFormErrors, setErrors, fetchRoomsForHotel, editingRoom, resetRoomForm) => {
  e.preventDefault();
  
  const errors = validateRoomForm();
  if (Object.keys(errors).length > 0) {
    setFormErrors(prev => ({ ...prev, room: errors }));
    return;
  }
  
  try {
    let submitData = { ...roomFormData };
    
    delete submitData.imageFile;
    
    if (submitData.price_per_night) submitData.price_per_night = parseFloat(submitData.price_per_night);
    if (submitData.capacity) submitData.capacity = parseInt(submitData.capacity);
    
    if (roomFormData.imageFile) {
      const formData = new FormData();
      formData.append('image', roomFormData.imageFile);
      
      try {
        const uploadResponse = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api'}/admin/upload`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            }
          }
        );
        
        if (uploadResponse.data.success) {
          submitData.image_url = uploadResponse.data.data.url;
        }
      } catch (uploadError) {
        console.error('Image upload error:', uploadError);
      }
    }
    
    let hotelId;
    if (editingRoom) {
      const response = await roomsAPI.update(editingRoom.id, submitData);
      hotelId = response.data?.data?.hotel_id || response.data?.hotel_id || submitData.hotel_id;
      if (hotelId) {
        // Fetch updated rooms for this hotel
        await fetchRoomsForHotel(hotelId);
      }
    } else {
      const response = await roomsAPI.create(submitData);
      const newRoom = response.data?.data || response.data;
      hotelId = newRoom.hotel_id || submitData.hotel_id;
      if (hotelId) {
        // Fetch updated rooms for this hotel
        await fetchRoomsForHotel(hotelId);
      }
    }
    
    resetRoomForm();
  } catch (error) {
    console.error('Error saving room:', error);
    setErrors(prev => ({ ...prev, roomForm: error.message || 'Failed to save room' }));
  }
};

/**
 * Handle Travelomatix room form submission
 * @param {Object} e - The event object
 * @param {Function} validateTravelomatixRoomForm - Function to validate Travelomatix room form
 * @param {Object} travelomatixRoomFormData - Current Travelomatix room form data
 * @param {Function} setTravelomatixFormErrors - State setter for Travelomatix form errors
 * @param {Function} formatRoomDataForSubmission - Function to format room data for submission
 * @param {Object} editingRoom - Current editing room
 * @param {Function} setRoomSearchError - State setter for room search error
 * @param {Function} fetchRoomsForHotel - Function to fetch rooms for a hotel
 * @param {Function} setShowTravelomatixRoomForm - State setter for showing Travelomatix room form
 * @param {Function} setTravelomatixRoomData - State setter for Travelomatix room data
 * @param {Function} setEditingRoom - State setter for editing room
 * @param {Function} setTravelomatixRoomFormData - State setter for Travelomatix room form data
 */
export const handleTravelomatixRoomSubmit = async (e, validateTravelomatixRoomForm, travelomatixRoomFormData, setTravelomatixFormErrors, formatRoomDataForSubmission, editingRoom, setRoomSearchError, fetchRoomsForHotel, setShowTravelomatixRoomForm, setTravelomatixRoomData, setEditingRoom, setTravelomatixRoomFormData) => {
  e.preventDefault();
  
  const errors = validateTravelomatixRoomForm();
  if (Object.keys(errors).length > 0) {
    setTravelomatixFormErrors(errors);
    return;
  }
  
  try {
    const submitData = formatRoomDataForSubmission(travelomatixRoomFormData);
    
    let response;
    if (editingRoom) {
      // Update existing room
      response = await roomsAPI.update(editingRoom.id, submitData);
      // Fetch updated rooms for this hotel
      await fetchRoomsForHotel(travelomatixRoomFormData.hotel_id);
    } else {
      // Create new room
      response = await roomsAPI.create(submitData);
      // Fetch updated rooms for this hotel
      await fetchRoomsForHotel(travelomatixRoomFormData.hotel_id);
    }
    
    if (response.data.success) {
      // Success message
      const message = editingRoom 
        ? 'Room updated successfully' 
        : 'Room added successfully to local database';
      alert(message);
      
      // Close the form
      setShowTravelomatixRoomForm(false);
      setTravelomatixRoomData(null);
      setEditingRoom(null);
      setTravelomatixRoomFormData({
        hotel_id: '',
        name_en: '',
        name_ar: '',
        supplier_id: '',
        room_type: '',
        // description_en: '',
        // description_ar: '',
        capacity: 1,
        price_per_night: '',
        currency: 'USD',
        images_urls: [''],
        // Add accessibility template IDs
        accessibility_template_ids: []
      });
    } else {
      throw new Error(response.data.message || (editingRoom ? 'Failed to update room' : 'Failed to add room'));
    }
  } catch (error) {
    console.error('Error saving room:', error);
    setRoomSearchError(error.message || (editingRoom ? 'Failed to update room' : 'Failed to add room to local database'));
  }
};

/**
 * Handle room search form submission
 * @param {Object} e - The event object
 * @param {Object} selectedHotelForRoomSearch - The selected hotel for room search
 * @param {Object} roomSearchParams - Current room search params
 * @param {Function} setRoomSearchParams - State setter for room search params
 * @param {Function} setRoomSearchError - State setter for room search error
 * @param {Function} searchRooms - Function to search rooms
 * @param {Function} setShowRoomSearchModal - State setter for showing room search modal
 */
export const handleRoomSearchSubmit = async (e, selectedHotelForRoomSearch, roomSearchParams, setRoomSearchParams, setRoomSearchError, searchRooms, setShowRoomSearchModal) => {
  e.preventDefault();
  
  // Validate required fields
  if (!roomSearchParams.resultToken || !roomSearchParams.checkInDate || !roomSearchParams.checkOutDate) {
    setRoomSearchError('Please fill in all required fields');
    return;
  }
  
  // Calculate number of nights
  const checkIn = new Date(roomSearchParams.checkInDate);
  const checkOut = new Date(roomSearchParams.checkOutDate);
  const nights = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
  
  // Update the noOfNights in the params
  const updatedParams = {
    ...roomSearchParams,
    noOfNights: nights
  };
  
  setRoomSearchParams(updatedParams);
  
  // Call the search function
  await searchRooms(
    selectedHotelForRoomSearch,
    updatedParams.resultToken,
    updatedParams.checkInDate,
    updatedParams.checkOutDate,
    updatedParams.adults,
    updatedParams.children
  );
  
  // Close the modal
  setShowRoomSearchModal(false);
};

export default {
  handleEditRoom,
  handleDeleteRoom,
  addRoomFromTravelomatix,
  handleRoomSubmit,
  handleTravelomatixRoomSubmit,
  handleRoomSearchSubmit
};